import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useT } from "@/lib/i18n";
import { Globe, Check, ChevronDown } from "lucide-react";

export default function LanguageSwitcher({ tone = "dark", variant = "desktop" }) {
    const { lang, setLang, languages } = useT();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = languages.find((l) => l.code === lang) || languages[0];
    const suffix = variant === "mobile" ? "-mobile" : "-desktop";

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const textColor = tone === "dark" ? "text-sn-silver" : "text-sn-ink";
    const borderColor = tone === "dark" ? "border-white/15" : "border-black/15";
    const hoverColor = tone === "dark" ? "hover:border-sn-blue" : "hover:border-sn-blue";

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`sn-lang-switcher${suffix}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border ${borderColor} ${textColor} ${hoverColor} font-mono-e text-[10px] uppercase tracking-[0.22em] transition-colors duration-300`}
            >
                <Globe size={13} className="text-sn-blue" />
                {current.label}
                <ChevronDown
                    size={12}
                    className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.ul
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        className={`absolute right-0 mt-2 min-w-[170px] rounded-lg border ${borderColor} bg-[rgba(8,8,12,0.95)] backdrop-blur-xl overflow-hidden z-50 shadow-2xl`}
                    >
                        {languages.map((l) => {
                            const active = l.code === lang;
                            return (
                                <li key={l.code}>
                                    <button
                                        type="button"
                                        data-testid={`sn-lang-option-${l.code}${suffix}`}
                                        onClick={() => {
                                            setLang(l.code);
                                            setOpen(false);
                                        }}
                                        className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 text-left font-mono-e text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${active ? "text-sn-blue" : "text-sn-silver hover:text-sn-blue"}`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <span className="text-sn-fog w-6">
                                                {l.label}
                                            </span>
                                            <span className="font-sans text-sn-silver/90 normal-case tracking-normal text-[13px]">
                                                {l.name}
                                            </span>
                                        </span>
                                        {active && (
                                            <Check
                                                size={12}
                                                className="text-sn-blue"
                                            />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
}
